/**
 * @file 学员充值 / 扣除弹窗
 * @description 按 100 步进调整点数；扣除不可超过当前余额。
 *
 * @whenToUse 学员列表行操作「充值」「扣除」。
 * @whenNotToUse 校区点数调整（用 CampusRechargeDialog）。
 */

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import type { StudentRow } from '@/components/pages/student-page/mock.ts'

/** 充值 / 扣除 */
export type StudentPointsDialogMode = 'recharge' | 'deduct'

export type StudentPointsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  student: StudentRow | null
  /** 充值 / 扣除；默认充值 */
  mode?: StudentPointsDialogMode
  onConfirm: (points: number, remark: string) => void
}

/**
 * 学员充值 / 扣除点数
 */
export function StudentPointsDialog({
  open,
  onOpenChange,
  student,
  mode = 'recharge',
  onConfirm,
}: StudentPointsDialogProps) {
  const isDeduct = mode === 'deduct'
  const [points, setPoints] = useState(100)
  const [remark, setRemark] = useState('')
  const [error, setError] = useState('')
  const balance = student?.points ?? 0

  useEffect(() => {
    if (!open) return
    setPoints(100)
    setRemark('')
    setError('')
  }, [open, mode])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isDeduct ? '扣除' : '充值'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            onClick={() => {
              if (!points || points <= 0) {
                setError(`请输入大于 0 的${isDeduct ? '扣除' : '充值'}点数`)
                return
              }
              if (isDeduct && points > balance) {
                setError('扣除点数不能超过当前余额')
                return
              }
              onConfirm(points, remark.trim())
              onOpenChange(false)
            }}
          >
            {isDeduct ? '确认扣除' : '确认充值'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          当前「{student?.name}」余额 {balance} 点。
        </p>
        <FormField
          label={isDeduct ? '扣除点数' : '充值点数'}
          required
          error={error}
        >
          <InputNumber
            value={points}
            min={100}
            max={isDeduct ? balance : undefined}
            step={100}
            invalid={Boolean(error)}
            onChange={(value) => setPoints(value ?? 0)}
            onBlur={() => {
              const snapped = Math.round(points / 100) * 100
              const next = Math.max(snapped, 100)
              setPoints(
                isDeduct && balance > 0 ? Math.min(next, balance) : next,
              )
            }}
          />
        </FormField>
        <FormField label="备注">
          <Textarea
            value={remark}
            placeholder="请输入备注(最多50字)"
            rows={3}
            maxLength={50}
            onChange={(event) => setRemark(event.target.value)}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
